import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import numeral from 'numeral';

interface WarehousesExportToCRMFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;

    meta: any;
    container_weight: any;
    line_template: any;
}

const state: WarehousesExportToCRMFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    container_weight: null,
    line_template: null,
    form: null,
    schema: null,

    meta: {},
};

const initState: WarehousesExportToCRMFormState = cloneDeep(state);

export const BASES_MANAGER_DATA = 'GET_BASES_MANAGER_DATA';
export const BASES_MANAGER_SCHEMA = 'GET_BASES_MANAGER_SCHEMA';
export const BASES_MANAGER_STORAGES = 'BASES_MANAGER_STORAGES';

export const actions: ActionTree<WarehousesExportToCRMFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        console.log('init', params)
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(BASES_MANAGER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
            ]).then((result: ServerResult) => {
                const _res = result.getOperation(BASES_MANAGER_DATA).getContent()
                commit('SET_FORM_DATA', {
                    document: _res.document,
                    schema: _res.schema,
                    params: params,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(BASES_MANAGER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: initParams.line_template == 'atom' ?  's:shift_atom_exported_to_crm' : 's:shift_custom_exported_to_crm'
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(BASES_MANAGER_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            saveQuery = vm.$q('MARK_DATA_SAVE')
                .create(state.line_template == 'atom' ?  's:shift_atom_exported_to_crm' : 's:shift_custom_exported_to_crm', normalizedForm)

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('MARK_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    addAttachments({state, dispatch}, {newValue}) {
      state.form.attachments = [
        newValue,
        ...(state.form.attachments ? state.form.attachments : []),
      ]
    },

};

export const mutations: MutationTree<WarehousesExportToCRMFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_BASES_MANAGER_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema, params}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
        state.container_weight = params.container_weight;
        state.line_template = params.line_template;

        state.form = generateFormObject(schema, state.document);
        if (params.documentPreview && params.documentPreview.state) {
            state.form.state = params.documentPreview.state
        }
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, {
            shift_atom_plan_id: initParams.shift_atom_plan_id,
            shift_custom_plan_id: initParams.shift_custom_plan_id,
            product_container_id: initParams.product_container_id,
            quantity: 0,
            weight: 0.0,
        });
        state.container_weight = initParams.container_weight;
        state.line_template = initParams.line_template;
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: WarehousesExportToCRMFormState, {field, newValue}) {
        switch (field) {
            case 'quantity':
                state.form[field] = newValue;
                state.form.weight = Math.round(newValue * state.container_weight * 100) / 100;
                break;
            case 'weight':
                state.form[field] = newValue;
                state.form.quantity = Math.ceil(newValue / state.container_weight);
                break;

            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: WarehousesExportToCRMFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_warehouses_export_to_crm_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
